import { z } from 'zod';

// Accepts a Firestore Timestamp-like, a Date, or an ISO string. Schemas stay
// dependency-free of firebase/firebase-admin; conversion is done at the boundary.
export const TimestampLike = z.union([
  z.date(),
  z.string().datetime(),
  z.object({
    seconds: z.number(),
    nanoseconds: z.number(),
  }),
  z.object({
    _seconds: z.number(),
    _nanoseconds: z.number(),
  }),
]);
export type TimestampLike = z.infer<typeof TimestampLike>;

export const Ownership = z.enum(['owned', 'earned', 'uncontrolled']);
export type Ownership = z.infer<typeof Ownership>;

export const Sentiment = z.enum(['positive', 'neutral', 'negative']);
export type Sentiment = z.infer<typeof Sentiment>;

export const ScoreBand = z.enum(['critical', 'at-risk', 'on-track', 'strong']);
export type ScoreBand = z.infer<typeof ScoreBand>;

export const Role = z.enum(['admin', 'project_coordinator', 'client']);
export type Role = z.infer<typeof Role>;

// safeText — Zod schema for human-entered text fields that we will display
// back to users (keyword names, client names, subject names). Rejects HTML
// angle brackets and control characters at the schema boundary so we never
// store payloads that could become XSS in any future surface — emails, PDFs,
// admin exports, etc. — even though current React rendering escapes them.
//
// Allows full Unicode (including accented + non-Latin scripts) and common
// punctuation (apostrophes, ampersands, parentheses), so legitimate names
// like "Johnson & Johnson" or "Cécile Müller" or "李明" still pass.
//
// Trims leading/trailing whitespace before validating length.
//
// Mirrored in firestore.rules via the `isSafeText()` helper for true
// server-side enforcement (the schema runs client-side; the rule cannot be
// bypassed). Keep the two in sync if either side changes.
//
// Rejecting control chars IS the point of this regex; the no-control-regex
// lint rule is a heuristic that doesn't apply here. Disabled inline.
// eslint-disable-next-line no-control-regex
const FORBIDDEN_CHARS_REGEX = /[<>\x00-\x1F\x7F]/;
export function safeText(label: string, opts: { min?: number; max?: number } = {}) {
  const min = opts.min ?? 1;
  const max = opts.max ?? 200;
  return z
    .string()
    .transform((s) => s.trim())
    .pipe(
      z
        .string()
        .min(min, `${label} is required`)
        .max(max, `${label} is too long (max ${max} characters)`)
        .refine((s) => !FORBIDDEN_CHARS_REGEX.test(s), {
          message: `${label} contains invalid characters (no <, >, or control characters)`,
        }),
    );
}
